/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Reelplicate API
 * OpenAPI spec version: 1.0
 */
import * as zod from 'zod';

/**
 * @summary The workspace's personas, oldest first, with the plan's allowance.
 */
export const ListPersonasParams = zod.object({
  id: zod.string(),
});

export const ListPersonasResponse = zod.object({
  items: zod
    .array(
      zod.object({
        id: zod.string(),
        name: zod.string(),
        shortName: zod
          .string()
          .nullable()
          .describe(
            'Shown on calendar chips. Null falls back to the first word of the name.',
          ),
        voice: zod.string().nullable().describe('Who they are and how they talk.'),
        timezone: zod
          .string()
          .nullable()
          .describe(
            "IANA timezone their posts are scheduled in. Null uses the workspace's.",
          ),
        avatarUrl: zod
          .string()
          .nullable()
          .describe('A short-lived link to the avatar image, or null without one.'),
        accountCount: zod.number().describe('Social accounts that post as this persona.'),
        accounts: zod
          .array(
            zod.object({
              platform: zod.enum([
                'TIKTOK',
                'INSTAGRAM',
                'YOUTUBE',
                'LINKEDIN',
                'FACEBOOK',
              ]),
              id: zod.string(),
              handle: zod.string(),
            }),
          )
          .describe('Those accounts, oldest first.'),
        reference: zod
          .array(
            zod.object({
              shot: zod.enum(['FACE', 'PROFILE', 'BACK', 'HALF_BODY', 'FULL_BODY']),
              url: zod.string().describe('A short-lived link to the photo.'),
            }),
          )
          .describe(
            'Their reference look: one photo per shot they have, in shot order\n(FACE, PROFILE, BACK, HALF_BODY, FULL_BODY). Image generation keeps\nthem looking like this.',
          ),
        referenceCount: zod.number().describe('How many of the five shots are there.'),
        createdAt: zod.iso.datetime({ offset: true }),
        updatedAt: zod.iso.datetime({ offset: true }),
      }),
    )
    .describe('Oldest first.'),
  limit: zod.number().describe("How many personas the workspace's plan allows."),
  used: zod.number().describe('How many it has.'),
});

/**
 * @summary Add a persona. Owners and editors, within the plan's allowance.
 */
export const CreatePersonaParams = zod.object({
  id: zod.string(),
});

export const createPersonaBodyNameMax = 60;

export const createPersonaBodyShortNameMax = 12;

export const createPersonaBodyVoiceMax = 2000;

export const CreatePersonaBody = zod.object({
  name: zod
    .string()
    .min(1)
    .max(createPersonaBodyNameMax)
    .describe('Unique within the workspace.'),
  shortName: zod
    .string()
    .max(createPersonaBodyShortNameMax)
    .optional()
    .describe('Shown on calendar chips; an empty string clears it.'),
  voice: zod
    .string()
    .max(createPersonaBodyVoiceMax)
    .optional()
    .describe('Who they are and how they talk; an empty string clears it.'),
  timezone: zod
    .string()
    .optional()
    .describe(
      "IANA timezone their posts are scheduled in; an empty string clears it,\nso the workspace's is used.",
    ),
});

export const CreatePersonaResponse = zod.object({
  id: zod.string(),
  name: zod.string(),
  shortName: zod
    .string()
    .nullable()
    .describe('Shown on calendar chips. Null falls back to the first word of the name.'),
  voice: zod.string().nullable().describe('Who they are and how they talk.'),
  timezone: zod
    .string()
    .nullable()
    .describe("IANA timezone their posts are scheduled in. Null uses the workspace's."),
  avatarUrl: zod
    .string()
    .nullable()
    .describe('A short-lived link to the avatar image, or null without one.'),
  accountCount: zod.number().describe('Social accounts that post as this persona.'),
  accounts: zod
    .array(
      zod.object({
        platform: zod.enum(['TIKTOK', 'INSTAGRAM', 'YOUTUBE', 'LINKEDIN', 'FACEBOOK']),
        id: zod.string(),
        handle: zod.string(),
      }),
    )
    .describe('Those accounts, oldest first.'),
  reference: zod
    .array(
      zod.object({
        shot: zod.enum(['FACE', 'PROFILE', 'BACK', 'HALF_BODY', 'FULL_BODY']),
        url: zod.string().describe('A short-lived link to the photo.'),
      }),
    )
    .describe(
      'Their reference look: one photo per shot they have, in shot order\n(FACE, PROFILE, BACK, HALF_BODY, FULL_BODY). Image generation keeps\nthem looking like this.',
    ),
  referenceCount: zod.number().describe('How many of the five shots are there.'),
  createdAt: zod.iso.datetime({ offset: true }),
  updatedAt: zod.iso.datetime({ offset: true }),
});

/**
 * @summary One persona, with its accounts and reference look.
 */
export const GetPersonaParams = zod.object({
  id: zod.string(),
  personaId: zod.string(),
});

export const GetPersonaResponse = zod.object({
  id: zod.string(),
  name: zod.string(),
  shortName: zod
    .string()
    .nullable()
    .describe('Shown on calendar chips. Null falls back to the first word of the name.'),
  voice: zod.string().nullable().describe('Who they are and how they talk.'),
  timezone: zod
    .string()
    .nullable()
    .describe("IANA timezone their posts are scheduled in. Null uses the workspace's."),
  avatarUrl: zod
    .string()
    .nullable()
    .describe('A short-lived link to the avatar image, or null without one.'),
  accountCount: zod.number().describe('Social accounts that post as this persona.'),
  accounts: zod
    .array(
      zod.object({
        platform: zod.enum(['TIKTOK', 'INSTAGRAM', 'YOUTUBE', 'LINKEDIN', 'FACEBOOK']),
        id: zod.string(),
        handle: zod.string(),
      }),
    )
    .describe('Those accounts, oldest first.'),
  reference: zod
    .array(
      zod.object({
        shot: zod.enum(['FACE', 'PROFILE', 'BACK', 'HALF_BODY', 'FULL_BODY']),
        url: zod.string().describe('A short-lived link to the photo.'),
      }),
    )
    .describe(
      'Their reference look: one photo per shot they have, in shot order\n(FACE, PROFILE, BACK, HALF_BODY, FULL_BODY). Image generation keeps\nthem looking like this.',
    ),
  referenceCount: zod.number().describe('How many of the five shots are there.'),
  createdAt: zod.iso.datetime({ offset: true }),
  updatedAt: zod.iso.datetime({ offset: true }),
});

/**
 * @summary Rename a persona or change its short name or voice.
 */
export const UpdatePersonaParams = zod.object({
  id: zod.string(),
  personaId: zod.string(),
});

export const updatePersonaBodyNameMax = 60;

export const updatePersonaBodyShortNameMax = 12;

export const updatePersonaBodyVoiceMax = 2000;

export const UpdatePersonaBody = zod.object({
  name: zod
    .string()
    .min(1)
    .max(updatePersonaBodyNameMax)
    .optional()
    .describe('Unique within the workspace.'),
  shortName: zod
    .string()
    .max(updatePersonaBodyShortNameMax)
    .optional()
    .describe('Shown on calendar chips; an empty string clears it.'),
  voice: zod
    .string()
    .max(updatePersonaBodyVoiceMax)
    .optional()
    .describe('Who they are and how they talk; an empty string clears it.'),
  timezone: zod
    .string()
    .optional()
    .describe(
      "IANA timezone their posts are scheduled in; an empty string clears it,\nso the workspace's is used.",
    ),
});

export const UpdatePersonaResponse = zod.object({
  id: zod.string(),
  name: zod.string(),
  shortName: zod
    .string()
    .nullable()
    .describe('Shown on calendar chips. Null falls back to the first word of the name.'),
  voice: zod.string().nullable().describe('Who they are and how they talk.'),
  timezone: zod
    .string()
    .nullable()
    .describe("IANA timezone their posts are scheduled in. Null uses the workspace's."),
  avatarUrl: zod
    .string()
    .nullable()
    .describe('A short-lived link to the avatar image, or null without one.'),
  accountCount: zod.number().describe('Social accounts that post as this persona.'),
  accounts: zod
    .array(
      zod.object({
        platform: zod.enum(['TIKTOK', 'INSTAGRAM', 'YOUTUBE', 'LINKEDIN', 'FACEBOOK']),
        id: zod.string(),
        handle: zod.string(),
      }),
    )
    .describe('Those accounts, oldest first.'),
  reference: zod
    .array(
      zod.object({
        shot: zod.enum(['FACE', 'PROFILE', 'BACK', 'HALF_BODY', 'FULL_BODY']),
        url: zod.string().describe('A short-lived link to the photo.'),
      }),
    )
    .describe(
      'Their reference look: one photo per shot they have, in shot order\n(FACE, PROFILE, BACK, HALF_BODY, FULL_BODY). Image generation keeps\nthem looking like this.',
    ),
  referenceCount: zod.number().describe('How many of the five shots are there.'),
  createdAt: zod.iso.datetime({ offset: true }),
  updatedAt: zod.iso.datetime({ offset: true }),
});

/**
 * @summary Delete a persona, with its social accounts.
 */
export const DeletePersonaParams = zod.object({
  id: zod.string(),
  personaId: zod.string(),
});

export const DeletePersonaResponse = zod.void();

/**
 * @summary Start an avatar upload: returns a short-lived URL to PUT the image to
(PNG, JPG, WebP or SVG, up to 2 MB), then call setPersonaAvatar with the key.
 */
export const CreatePersonaAvatarUploadParams = zod.object({
  id: zod.string(),
  personaId: zod.string(),
});

export const createPersonaAvatarUploadBodySizeMax = 2097152;

export const CreatePersonaAvatarUploadBody = zod.object({
  contentType: zod.enum(['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml']),
  size: zod
    .number()
    .min(1)
    .max(createPersonaAvatarUploadBodySizeMax)
    .describe('File size in bytes, up to 2 MB.'),
});

export const CreatePersonaAvatarUploadResponse = zod.object({
  url: zod
    .string()
    .describe('PUT the file here, with the same Content-Type, before it expires.'),
  key: zod.string().describe('Send this back once the upload finishes.'),
  expiresIn: zod.number().describe('Seconds until `url` stops working.'),
});

/**
 * @summary Use an uploaded image as the persona's avatar, replacing the previous one.
 */
export const SetPersonaAvatarParams = zod.object({
  id: zod.string(),
  personaId: zod.string(),
});

export const setPersonaAvatarBodyKeyMax = 512;

export const SetPersonaAvatarBody = zod.object({
  key: zod
    .string()
    .max(setPersonaAvatarBodyKeyMax)
    .describe('The key the upload call returned.'),
});

export const SetPersonaAvatarResponse = zod.object({
  id: zod.string(),
  name: zod.string(),
  shortName: zod
    .string()
    .nullable()
    .describe('Shown on calendar chips. Null falls back to the first word of the name.'),
  voice: zod.string().nullable().describe('Who they are and how they talk.'),
  timezone: zod
    .string()
    .nullable()
    .describe("IANA timezone their posts are scheduled in. Null uses the workspace's."),
  avatarUrl: zod
    .string()
    .nullable()
    .describe('A short-lived link to the avatar image, or null without one.'),
  accountCount: zod.number().describe('Social accounts that post as this persona.'),
  accounts: zod
    .array(
      zod.object({
        platform: zod.enum(['TIKTOK', 'INSTAGRAM', 'YOUTUBE', 'LINKEDIN', 'FACEBOOK']),
        id: zod.string(),
        handle: zod.string(),
      }),
    )
    .describe('Those accounts, oldest first.'),
  reference: zod
    .array(
      zod.object({
        shot: zod.enum(['FACE', 'PROFILE', 'BACK', 'HALF_BODY', 'FULL_BODY']),
        url: zod.string().describe('A short-lived link to the photo.'),
      }),
    )
    .describe(
      'Their reference look: one photo per shot they have, in shot order\n(FACE, PROFILE, BACK, HALF_BODY, FULL_BODY). Image generation keeps\nthem looking like this.',
    ),
  referenceCount: zod.number().describe('How many of the five shots are there.'),
  createdAt: zod.iso.datetime({ offset: true }),
  updatedAt: zod.iso.datetime({ offset: true }),
});

/**
 * @summary Remove the persona's avatar.
 */
export const RemovePersonaAvatarParams = zod.object({
  id: zod.string(),
  personaId: zod.string(),
});

export const RemovePersonaAvatarResponse = zod.object({
  id: zod.string(),
  name: zod.string(),
  shortName: zod
    .string()
    .nullable()
    .describe('Shown on calendar chips. Null falls back to the first word of the name.'),
  voice: zod.string().nullable().describe('Who they are and how they talk.'),
  timezone: zod
    .string()
    .nullable()
    .describe("IANA timezone their posts are scheduled in. Null uses the workspace's."),
  avatarUrl: zod
    .string()
    .nullable()
    .describe('A short-lived link to the avatar image, or null without one.'),
  accountCount: zod.number().describe('Social accounts that post as this persona.'),
  accounts: zod
    .array(
      zod.object({
        platform: zod.enum(['TIKTOK', 'INSTAGRAM', 'YOUTUBE', 'LINKEDIN', 'FACEBOOK']),
        id: zod.string(),
        handle: zod.string(),
      }),
    )
    .describe('Those accounts, oldest first.'),
  reference: zod
    .array(
      zod.object({
        shot: zod.enum(['FACE', 'PROFILE', 'BACK', 'HALF_BODY', 'FULL_BODY']),
        url: zod.string().describe('A short-lived link to the photo.'),
      }),
    )
    .describe(
      'Their reference look: one photo per shot they have, in shot order\n(FACE, PROFILE, BACK, HALF_BODY, FULL_BODY). Image generation keeps\nthem looking like this.',
    ),
  referenceCount: zod.number().describe('How many of the five shots are there.'),
  createdAt: zod.iso.datetime({ offset: true }),
  updatedAt: zod.iso.datetime({ offset: true }),
});

/**
 * @summary Start a reference photo upload for one shot of the persona's reference
look: returns a short-lived URL to PUT the photo to (JPG, PNG or WebP,
up to 20 MB), then call setPersonaReference with the key.
 */
export const CreatePersonaReferenceUploadParams = zod.object({
  id: zod.string(),
  personaId: zod.string(),
  shot: zod.enum(['FACE', 'PROFILE', 'BACK', 'HALF_BODY', 'FULL_BODY']),
});

export const createPersonaReferenceUploadBodySizeMax = 20971520;

export const CreatePersonaReferenceUploadBody = zod.object({
  contentType: zod.enum(['image/png', 'image/jpeg', 'image/webp']),
  size: zod
    .number()
    .min(1)
    .max(createPersonaReferenceUploadBodySizeMax)
    .describe('File size in bytes, up to 20 MB.'),
});

export const CreatePersonaReferenceUploadResponse = zod.object({
  url: zod
    .string()
    .describe('PUT the file here, with the same Content-Type, before it expires.'),
  key: zod.string().describe('Send this back once the upload finishes.'),
  expiresIn: zod.number().describe('Seconds until `url` stops working.'),
});

/**
 * @summary Use an uploaded photo for one shot of the reference look, replacing the previous one.
 */
export const SetPersonaReferenceParams = zod.object({
  id: zod.string(),
  personaId: zod.string(),
  shot: zod.enum(['FACE', 'PROFILE', 'BACK', 'HALF_BODY', 'FULL_BODY']),
});

export const setPersonaReferenceBodyKeyMax = 512;

export const SetPersonaReferenceBody = zod.object({
  key: zod
    .string()
    .max(setPersonaReferenceBodyKeyMax)
    .describe('The key the upload call returned.'),
});

export const SetPersonaReferenceResponse = zod.object({
  id: zod.string(),
  name: zod.string(),
  shortName: zod
    .string()
    .nullable()
    .describe('Shown on calendar chips. Null falls back to the first word of the name.'),
  voice: zod.string().nullable().describe('Who they are and how they talk.'),
  timezone: zod
    .string()
    .nullable()
    .describe("IANA timezone their posts are scheduled in. Null uses the workspace's."),
  avatarUrl: zod
    .string()
    .nullable()
    .describe('A short-lived link to the avatar image, or null without one.'),
  accountCount: zod.number().describe('Social accounts that post as this persona.'),
  accounts: zod
    .array(
      zod.object({
        platform: zod.enum(['TIKTOK', 'INSTAGRAM', 'YOUTUBE', 'LINKEDIN', 'FACEBOOK']),
        id: zod.string(),
        handle: zod.string(),
      }),
    )
    .describe('Those accounts, oldest first.'),
  reference: zod
    .array(
      zod.object({
        shot: zod.enum(['FACE', 'PROFILE', 'BACK', 'HALF_BODY', 'FULL_BODY']),
        url: zod.string().describe('A short-lived link to the photo.'),
      }),
    )
    .describe(
      'Their reference look: one photo per shot they have, in shot order\n(FACE, PROFILE, BACK, HALF_BODY, FULL_BODY). Image generation keeps\nthem looking like this.',
    ),
  referenceCount: zod.number().describe('How many of the five shots are there.'),
  createdAt: zod.iso.datetime({ offset: true }),
  updatedAt: zod.iso.datetime({ offset: true }),
});

/**
 * @summary Remove the photo for one shot of the reference look.
 */
export const RemovePersonaReferenceParams = zod.object({
  id: zod.string(),
  personaId: zod.string(),
  shot: zod.enum(['FACE', 'PROFILE', 'BACK', 'HALF_BODY', 'FULL_BODY']),
});

export const RemovePersonaReferenceResponse = zod.object({
  id: zod.string(),
  name: zod.string(),
  shortName: zod
    .string()
    .nullable()
    .describe('Shown on calendar chips. Null falls back to the first word of the name.'),
  voice: zod.string().nullable().describe('Who they are and how they talk.'),
  timezone: zod
    .string()
    .nullable()
    .describe("IANA timezone their posts are scheduled in. Null uses the workspace's."),
  avatarUrl: zod
    .string()
    .nullable()
    .describe('A short-lived link to the avatar image, or null without one.'),
  accountCount: zod.number().describe('Social accounts that post as this persona.'),
  accounts: zod
    .array(
      zod.object({
        platform: zod.enum(['TIKTOK', 'INSTAGRAM', 'YOUTUBE', 'LINKEDIN', 'FACEBOOK']),
        id: zod.string(),
        handle: zod.string(),
      }),
    )
    .describe('Those accounts, oldest first.'),
  reference: zod
    .array(
      zod.object({
        shot: zod.enum(['FACE', 'PROFILE', 'BACK', 'HALF_BODY', 'FULL_BODY']),
        url: zod.string().describe('A short-lived link to the photo.'),
      }),
    )
    .describe(
      'Their reference look: one photo per shot they have, in shot order\n(FACE, PROFILE, BACK, HALF_BODY, FULL_BODY). Image generation keeps\nthem looking like this.',
    ),
  referenceCount: zod.number().describe('How many of the five shots are there.'),
  createdAt: zod.iso.datetime({ offset: true }),
  updatedAt: zod.iso.datetime({ offset: true }),
});

/**
 * @summary Every post found on the persona's accounts, with its numbers on each platform, newest first.
 */
export const ListPersonaPostsParams = zod.object({
  id: zod.string(),
  personaId: zod.string(),
});

export const ListPersonaPostsResponse = zod.object({
  items: zod
    .array(
      zod.object({
        kind: zod.union([zod.enum(['VIDEO', 'CAROUSEL', 'IMAGE']), zod.null()]),
        id: zod
          .string()
          .describe(
            "The Calendar post's id when it is one, else `<accountId>:<platform id>`.",
          ),
        title: zod.string().describe("The Calendar title, or the caption's first line."),
        postedAt: zod.iso
          .datetime({ offset: true })
          .nullable()
          .describe('When it first went out on any of its accounts.'),
        thumbnailUrl: zod
          .string()
          .nullable()
          .describe("The cover, presigned or the platform's own (both expire)."),
        calendarPostId: zod
          .string()
          .nullable()
          .describe('The Calendar post it is, when it was scheduled in Reelplicate.'),
        platforms: zod.array(
          zod.object({
            platform: zod.enum([
              'TIKTOK',
              'INSTAGRAM',
              'YOUTUBE',
              'LINKEDIN',
              'FACEBOOK',
            ]),
            accountId: zod.string(),
            handle: zod.string(),
            url: zod.string().nullable().describe('Opens the post on the platform.'),
            postedAt: zod.iso.datetime({ offset: true }).nullable(),
            views: zod
              .number()
              .nullable()
              .describe(
                "Null means the platform doesn't report it (Instagram: shares, saves).",
              ),
            likes: zod.number().nullable(),
            comments: zod.number().nullable(),
            shares: zod.number().nullable(),
            saves: zod.number().nullable(),
            engagement: zod
              .number()
              .nullable()
              .describe(
                'Likes, comments, shares and saves per view, in percent; null without views.',
              ),
            measuredAt: zod.iso
              .datetime({ offset: true })
              .describe('When these counts were read.'),
          }),
        ),
      }),
    )
    .describe('Newest first.'),
});
